import { X, ShoppingBag, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';

const getFirstImage = (product) => {
  if (!product || !product.images) return '';
  return product.images.length > 0 ? product.images[0] : '';
};

const CartDrawer = ({ open, onClose }) => {
  const { cart, loading, updateQuantity, removeFromCart, cartTotal, cartCount } = useCart();

  const handleDecrement = (productId, currentQty) => {
    updateQuantity(productId, Math.max(1, currentQty - 1));
  };

  const handleIncrement = (productId, currentQty) => {
    updateQuantity(productId, currentQty + 1);
  };

  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  const renderCartItem = (item) => {
    const product = item.product;
    const price = product?.sale_price || product?.price || 0;
    const total = price * item.quantity;
    const imgSrc = getFirstImage(product);

    return (
      <div key={item.product_id} className="flex gap-4" data-testid={`cart-item-${item.product_id}`}>
        <div className="w-20 h-20 bg-[#F9F7F2] flex-shrink-0">
          {imgSrc && <img src={imgSrc} alt={product?.name || ''} className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-[#1A1A1A] truncate">{product?.name || 'Product'}</h4>
          <p className="text-sm text-[#6B7280]">{product?.style} • {product?.color}</p>
          <div className="flex items-center justify-between mt-2">
            <div className="quantity-selector">
              <button className="quantity-btn" onClick={() => handleDecrement(item.product_id, item.quantity)} disabled={loading}>-</button>
              <span className="quantity-input">{item.quantity}</span>
              <button className="quantity-btn" onClick={() => handleIncrement(item.product_id, item.quantity)} disabled={loading}>+</button>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-medium text-[#1A1A1A]">${total.toFixed(2)}</span>
              <button onClick={() => removeFromCart(item.product_id)} className="p-1 text-[#6B7280] hover:text-red-500" disabled={loading}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`overlay ${open ? 'active' : ''}`} onClick={onClose} data-testid="cart-overlay" />

      <div className={`cart-drawer ${open ? 'open' : ''}`} data-testid="cart-drawer">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-3">
              <ShoppingBag size={24} className="text-[#1A1A1A]" />
              <h2 className="font-manrope text-xl font-semibold">Your Cart</h2>
              <span className="text-sm text-[#6B7280]">({cartCount} items)</span>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[#F9F7F2] rounded-md" data-testid="close-cart-button">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {isEmpty ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <ShoppingBag size={48} className="text-[#E5E7EB] mb-4" />
                <h3 className="font-manrope text-lg font-medium text-[#1A1A1A] mb-2">Your cart is empty</h3>
                <p className="text-[#6B7280] mb-6">Start shopping to add items to your cart</p>
                <Button onClick={onClose} asChild>
                  <Link to="/shop" data-testid="start-shopping-link">Start Shopping</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-6">{items.map(renderCartItem)}</div>
            )}
          </div>

          {!isEmpty && (
            <div className="border-t border-[#E5E7EB] p-6 bg-[#F9F7F2]">
              <div className="flex justify-between items-center mb-4">
                <span className="text-[#6B7280]">Subtotal</span>
                <span className="font-manrope text-xl font-semibold text-[#1A1A1A]">${cartTotal.toFixed(2)}</span>
              </div>
              <p className="text-sm text-[#6B7280] mb-4">Shipping calculated at checkout</p>
              <div className="space-y-3">
                <Button className="w-full bg-[#1A1A1A] hover:bg-[#333333] text-white" asChild onClick={onClose}>
                  <Link to="/checkout" data-testid="checkout-button">Proceed to Checkout</Link>
                </Button>
                <Button variant="outline" className="w-full border-[#1A1A1A] text-[#1A1A1A]" asChild onClick={onClose}>
                  <Link to="/cart" data-testid="view-cart-button">View Cart</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartDrawer;
